import { toAdminProspectImportPreviewRequestRows } from '../../components/admin/prospectImportMapping.js';

export const ADMIN_PROSPECTS_PAGE_SIZE = 25;

export const ADMIN_PROSPECT_LOOKUP_TYPES = Object.freeze(['address', 'city', 'postalCode']);

export const ADMIN_PROSPECT_STATUSES = Object.freeze([
  'new',
  'contacted',
  'follow_up',
  'owner_responded',
  'qualified',
  'not_interested',
  'converted',
]);

export const ADMIN_PROSPECT_ACTIVITY_TYPES = Object.freeze(['note', 'status_change', 'public_response']);

export const CANADA_PROVINCE_CODES = Object.freeze([
  'QC',
  'ON',
  'NB',
  'NS',
  'PE',
  'NL',
  'MB',
  'SK',
  'AB',
  'BC',
  'YT',
  'NT',
  'NU',
]);

function clampPageSize(pageSize) {
  const size = Number(pageSize);
  return Number.isFinite(size) && size > 0 ? Math.floor(size) : ADMIN_PROSPECTS_PAGE_SIZE;
}

function optionalTrim(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * listAdminProspects request. Empty query → browse (optionally status-filtered).
 * One lookupType at a time. Opaque pageToken only — no offset.
 * @param {{
 *   pageSize?: number,
 *   pageToken?: string | null,
 *   lookupType?: string | null,
 *   query?: string | null,
 *   status?: string | null,
 *   targeting?: Record<string, unknown> | null,
 *   includeTotalCount?: boolean,
 * }} [params]
 */
export function buildListAdminProspectsRequest({
  pageSize = ADMIN_PROSPECTS_PAGE_SIZE,
  pageToken,
  lookupType,
  query,
  status,
  targeting,
  includeTotalCount,
} = {}) {
  const request = {
    pageSize: clampPageSize(pageSize),
  };
  if (targeting && typeof targeting === 'object' && !Array.isArray(targeting)) {
    request.targeting = targeting;
    if (includeTotalCount === true) request.includeTotalCount = true;
    if (pageToken) request.pageToken = String(pageToken);
    return request;
  }
  const type = optionalTrim(lookupType);
  const q = optionalTrim(query);
  if (type && q) {
    request.lookupType = type;
    request.query = q;
  }
  const nextStatus = optionalTrim(status);
  if (nextStatus) request.status = nextStatus;
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

/**
 * createAdminProspect payload. Omits empty optionals. Never sends source, status,
 * or allowDuplicate — backend defaults (manual / new) stay authoritative.
 * @param {{
 *   address?: string,
 *   city?: string,
 *   province?: string,
 *   postalCode?: string,
 *   numberOfUnits?: number | string | null,
 *   ownerName?: string | null,
 *   ownerMailingAddress?: string | null,
 *   notes?: string | null,
 * }} fields
 */
export function buildCreateAdminProspectRequest(fields = {}) {
  const request = {
    address: optionalTrim(fields.address),
    city: optionalTrim(fields.city),
    province: optionalTrim(fields.province),
    postalCode: optionalTrim(fields.postalCode),
  };
  if (Object.prototype.hasOwnProperty.call(fields, 'numberOfUnits')) {
    const raw = fields.numberOfUnits;
    if (raw !== '' && raw != null) {
      const n = Number(raw);
      if (Number.isFinite(n)) request.numberOfUnits = n;
    }
  }
  const ownerName = optionalTrim(fields.ownerName);
  if (ownerName) request.ownerName = ownerName;
  const ownerMailingAddress = optionalTrim(fields.ownerMailingAddress);
  if (ownerMailingAddress) request.ownerMailingAddress = ownerMailingAddress;
  const notes = optionalTrim(fields.notes);
  if (notes) request.notes = notes;
  return request;
}

/**
 * updateAdminProspect payload. Empty optionals are null (backend deletes).
 * Never sends source or allowDuplicate.
 * @param {string} prospectId
 * @param {Record<string, unknown>} fields
 */
export function buildUpdateAdminProspectRequest(prospectId, fields = {}) {
  const patch = {
    address: optionalTrim(fields.address),
    city: optionalTrim(fields.city),
    province: optionalTrim(fields.province),
    postalCode: optionalTrim(fields.postalCode),
    status: optionalTrim(fields.status),
  };
  if (fields.numberOfUnits === '' || fields.numberOfUnits == null) {
    patch.numberOfUnits = null;
  } else {
    const n = Number(fields.numberOfUnits);
    if (Number.isFinite(n)) patch.numberOfUnits = n;
  }
  patch.ownerName = optionalTrim(fields.ownerName) || null;
  patch.ownerMailingAddress = optionalTrim(fields.ownerMailingAddress) || null;
  patch.notes = optionalTrim(fields.notes) || null;
  patch.lastContactAt = optionalTrim(fields.lastContactAt) || null;
  patch.nextFollowUpAt = optionalTrim(fields.nextFollowUpAt) || null;
  return { prospectId: String(prospectId || '').trim(), patch };
}

/** Sparse CRM follow-up keys only — never address/notes/owner. */
export const ADMIN_PROSPECT_FOLLOW_UP_PATCH_KEYS = Object.freeze([
  'status',
  'lastContactAt',
  'nextFollowUpAt',
]);

/**
 * Sparse updateAdminProspect payload for Immo 360 quick follow-up.
 * Only includes keys present on `fields`. Explicit null clears optional dates.
 * Omitted keys are preserved server-side. Never resubmits address/notes/owner.
 * Optional expectedUpdatedAt / expectedUpdatedAtVersion enable conflict detection.
 * @param {string} prospectId
 * @param {{ status?: string, lastContactAt?: string | null, nextFollowUpAt?: string | null }} fields
 * @param {{ s: number, n: number } | string | null} [version]
 */
export function buildPatchAdminProspectFollowUpRequest(prospectId, fields = {}, version = null) {
  const patch = {};
  for (const key of ADMIN_PROSPECT_FOLLOW_UP_PATCH_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(fields, key)) continue;
    if (key === 'status') {
      patch.status = optionalTrim(fields.status);
      continue;
    }
    const value = fields[key];
    if (value === null) {
      patch[key] = null;
      continue;
    }
    patch[key] = optionalTrim(value);
  }
  const request = { prospectId: String(prospectId || '').trim(), patch };
  if (version && typeof version === 'object' && Number.isInteger(version.s) && Number.isInteger(version.n)) {
    request.expectedUpdatedAtVersion = { s: version.s, n: version.n };
  } else if (typeof version === 'string' && version.trim()) {
    request.expectedUpdatedAt = version.trim();
  }
  return request;
}

export function buildListAdminProspectActivitiesRequest({
  prospectId,
  pageSize = ADMIN_PROSPECTS_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    prospectId: String(prospectId || '').trim(),
    pageSize: clampPageSize(pageSize),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

export function buildListAdminProspectImportHistoryRequest({
  prospectId,
  pageSize = ADMIN_PROSPECTS_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    prospectId: String(prospectId || '').trim(),
    pageSize: clampPageSize(pageSize),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

export function buildListAdminProspectAssociatedCampaignsRequest({
  prospectId,
  pageSize = ADMIN_PROSPECTS_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    prospectId: String(prospectId || '').trim(),
    pageSize: clampPageSize(pageSize),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

export function buildAddAdminProspectNoteRequest({ prospectId, body } = {}) {
  return {
    prospectId: String(prospectId || '').trim(),
    body: optionalTrim(body),
  };
}

const PROJECTION_SECTIONS = new Set([
  'primaryContact',
  'latestOwnerProvidedPropertyProfile',
  'latestOwnerProvidedFinancialSnapshot',
]);

const PROJECTION_VERIFICATION_STATES = new Set(['unverified', 'admin_verified']);

const PROJECTION_INTENTS = new Set([
  'profitability_guide',
  'property_value',
  'property_sale',
  'legacy_owner_response',
]);

export function buildSetAdminProspectProjectionVerificationRequest({
  prospectId,
  section,
  verificationState,
  expectedVerificationState,
  expectedSource,
} = {}) {
  const source = expectedSource && typeof expectedSource === 'object' && !Array.isArray(expectedSource)
    ? expectedSource
    : {};
  return {
    prospectId: String(prospectId || '').trim(),
    section: PROJECTION_SECTIONS.has(section) ? section : '',
    verificationState: PROJECTION_VERIFICATION_STATES.has(verificationState) ? verificationState : '',
    expectedVerificationState: PROJECTION_VERIFICATION_STATES.has(expectedVerificationState)
      ? expectedVerificationState
      : '',
    expectedSource: {
      campaignId: optionalTrim(source.campaignId),
      mailingId: optionalTrim(source.mailingId),
      responseIntent: PROJECTION_INTENTS.has(source.responseIntent) ? source.responseIntent : '',
      submittedAt: optionalTrim(source.submittedAt),
    },
  };
}

export const ADMIN_PROSPECT_IMPORT_MAX_ROWS = 500;
export const ADMIN_PROSPECT_IMPORT_INPUT_MODES = Object.freeze([
  'paste',
  'csv',
  'xlsx',
  'file_csv',
  'file_excel',
]);

function normalizeImportInputMode(inputMode) {
  const mode = optionalTrim(inputMode);
  if (mode === 'csv') return 'csv';
  if (mode === 'xlsx') return 'xlsx';
  if (mode === 'file_csv') return 'file_csv';
  if (mode === 'file_excel') return 'file_excel';
  return 'paste';
}

export function buildPreviewAdminProspectImportRequest({ inputMode, rows, importId } = {}) {
  const normalized = normalizeImportInputMode(inputMode);
  const request = {
    inputMode: normalized,
    // Always project mapped rows onto the wire allowlist before the callable.
    rows: toAdminProspectImportPreviewRequestRows(Array.isArray(rows) ? rows : []),
  };
  if (normalized === 'xlsx' || normalized === 'file_excel' || normalized === 'file_csv') {
    request.importId = String(importId || '').trim();
  }
  return request;
}

export function buildCommitAdminProspectImportRequest({
  importId,
  excludedRowNumbers,
  confirmedUpdateRowNumbers,
  matchResolutions,
} = {}) {
  const request = {
    importId: String(importId || '').trim(),
    excludedRowNumbers: Array.isArray(excludedRowNumbers)
      ? excludedRowNumbers.filter((value) => Number.isInteger(value) && value > 0)
      : [],
  };
  if (Array.isArray(confirmedUpdateRowNumbers)) {
    request.confirmedUpdateRowNumbers = confirmedUpdateRowNumbers.filter(
      (value) => Number.isInteger(value) && value > 0,
    );
  }
  if (Array.isArray(matchResolutions)) {
    request.matchResolutions = matchResolutions
      .filter((item) => item && typeof item === 'object' && Number.isInteger(item.rowNumber) && item.rowNumber > 0)
      .map((item) =>
        item.skip === true
          ? { rowNumber: item.rowNumber, skip: true }
          : { rowNumber: item.rowNumber, prospectId: String(item.prospectId || '').trim() },
      );
  }
  return request;
}

const PROPERTY_DATA_PATCH_KEYS = new Set([
  'lotNumbers',
  'cadastralDivision',
  'municipalRollNumber',
  'numberOfFloors',
  'constructionYear',
  'landArea',
  'municipalAssessment',
  'registryActivity',
  'numberOfUnits',
]);

/**
 * updateAdminProspectPropertyData payload. Omitted nested keys are not sent.
 * Explicit null clears optional fields on the server.
 */
export function buildUpdateAdminProspectPropertyDataRequest(prospectId, fields = {}, version = null) {
  const patch = {};
  for (const key of PROPERTY_DATA_PATCH_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(fields, key)) continue;
    patch[key] = fields[key];
  }
  const request = { prospectId: String(prospectId || '').trim(), patch };
  if (version && typeof version === 'object' && Number.isInteger(version.s) && Number.isInteger(version.n)) {
    request.expectedUpdatedAtVersion = { s: version.s, n: version.n };
  } else if (typeof version === 'string' && version.trim()) {
    request.expectedUpdatedAt = version.trim();
  }
  return request;
}

export function buildCreateAdminProspectImportUploadRequest({ fileType, contentType, byteSize } = {}) {
  const type = optionalTrim(fileType);
  return {
    fileType: type === 'xls' ? 'xls' : type === 'csv' ? 'csv' : 'xlsx',
    contentType: String(contentType || '').trim(),
    byteSize: Number.isInteger(byteSize) ? byteSize : 0,
  };
}

export function buildParseAdminProspectImportFileRequest({ importId, delimiter, hasHeader } = {}) {
  const request = { importId: String(importId || '').trim() };
  if (typeof delimiter === 'string' && delimiter.trim()) {
    request.delimiter = delimiter.trim();
  }
  if (typeof hasHeader === 'boolean') request.hasHeader = hasHeader;
  return request;
}

export function buildConfirmAdminProspectImportMappingRequest({ importId, mapping, profile } = {}) {
  return {
    importId: String(importId || '').trim(),
    mapping: Array.isArray(mapping) ? mapping : [],
    profile: profile && typeof profile === 'object' && !Array.isArray(profile) ? profile : {},
  };
}

export function buildGetAdminProspectImportJobRequest({ importId } = {}) {
  return { importId: String(importId || '').trim() };
}

export function buildSuggestAdminProspectImportMappingRequest({ sourceProfileCandidate, columns } = {}) {
  return {
    sourceProfileCandidate: sourceProfileCandidate === 'jlr' ? 'jlr' : 'generic',
    columns: Array.isArray(columns) ? columns : [],
  };
}
